import { useMutation, useQuery } from '@tanstack/react-query'

import {
  AuditDiscrepancyTask,
  AuditTask,
  AuditTaskResolutionFeatureAnnotation,
  Media,
  PipelineJob,
  Prediction,
} from '@sparkai/shared-types'

import { apiClient } from '../apiClient'

export const GET_AUDIT_QUEUE_QUERY_KEY = 'audit-queue-status-v3'

type AuditQueueSummary = {
  program_id: string
  name: string
  display_name: string
  total_tasks: number
  last_day_tasks: number
  partially_audited_count: number
  audit_discrepancy_count: number
}

type AutomatedAuditStatus = {
  total_tasks: number
  last_day_tasks: number
  partially_audited_engagements: number
  discrepancy_engagements: number
  queues: AuditQueueSummary[]
}

export function useGetAutomatedAuditStatus() {
  return useQuery({
    queryKey: ['automated-audit-status'],
    queryFn: async () => {
      const { data } = await apiClient.get<AutomatedAuditStatus>('/int/v2/audit')
      return data
    },
  })
}
export type QueueStats = {
  total: number
  last24Hrs: number
  partiallyAudited: number
  discrepancies: number
}
export type ProgramStats = {
  id: string
  displayName: string
  color: string
  iconType: string
  queue: QueueStats
}
type AutomatedAuditStatusV3 = {
  overall: QueueStats
  programs: ProgramStats[]
}

export function useGetAutomatedAuditStatusV3({ enabled }: { enabled: boolean }) {
  return useQuery({
    queryKey: [GET_AUDIT_QUEUE_QUERY_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<AutomatedAuditStatusV3>('/int/v3/engagements/audit/queue')
      return data
    },
    enabled,
  })
}

type StartAuditQueueSessionResponse = {
  auditQueueTasksIds: string[]
}
export function usePostStartQueueSession(programId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['audit-queue-start-session', programId],
    queryFn: async () => {
      const { data } = await apiClient.post<StartAuditQueueSessionResponse>(
        '/int/v3/engagements/audit/queue/session',
        { programId, limit: 50 }
      )
      return data
    },
    enabled: options?.enabled ?? true,
  })
}

export function usePostEndQueueSession() {
  return useQuery({
    queryKey: ['audit-queue-end-session'],
    queryFn: async () => {
      const { status } = await apiClient.post<StartAuditQueueSessionResponse>(
        '/int/v3/engagements/audit/queue/session/end'
      )

      return status
    },
  })
}

export function useGetAuditTaskDataById(auditQueueTaskId?: string) {
  return useQuery({
    queryKey: ['audit-queue-get-audit-task-by-id', auditQueueTaskId],
    enabled: !!auditQueueTaskId,
    queryFn: async () => {
      const { data } = await apiClient.get<AuditTask>(
        `/int/v3/engagements/audit/queue/session/${auditQueueTaskId}`
      )

      return data
    },
  })
}

type PostDiscrepancySession = {
  discrepancies: AuditDiscrepancyTask[]
}
export function usePostAuditDiscrepancySession(programId?: string) {
  return useQuery({
    queryKey: ['get-audit-discrepancy-queue', programId],
    queryFn: async () => {
      const { data } = await apiClient.post<PostDiscrepancySession>(
        `/int/v3/engagements/audit/discrepancy/session`,
        { programId, limit: 50 }
      )

      return data
    },
  })
}

type PostAuditTaskResolution = {
  response: string
  annotations: AuditTaskResolutionFeatureAnnotation[]
  skip: boolean
  includeInBenchmark?: boolean
  isEscalated?: boolean
  escalationReason?: string | null
}
export function usePostAuditTaskResolution(auditQueueTaskId?: string) {
  return useMutation({
    mutationFn: async (resolution: PostAuditTaskResolution) => {
      if (auditQueueTaskId) {
        const { data, status } = await apiClient.post<{ groundTruthId: string }>(
          `/int/v3/engagements/audit/queue/session/${auditQueueTaskId}`,
          resolution
        )

        return { data, status }
      }
    },
  })
}

export type ManualAuditTaskResolution = {
  auditQueueTaskId: string
  engagementId?: string
  taskId?: string
  response?: string
  annotations?: AuditTaskResolutionFeatureAnnotation[]
  includeInBenchmark?: boolean
  skip?: boolean
  isEscalated?: boolean
  escalationReason?: string | null
}
export function usePostManualAuditTaskResolution() {
  return useMutation({
    mutationFn: async ({
      auditQueueTaskId,
      engagementId,
      taskId,
      response,
      annotations,
      includeInBenchmark,
      skip,
      isEscalated,
      escalationReason,
    }: ManualAuditTaskResolution) => {
      const payload = skip
        ? { auditQueueTaskId, skip: true }
        : {
            auditQueueTaskId,
            engagementId,
            taskId,
            response,
            annotations: annotations ?? [],
            includeInBenchmark,
            ...(isEscalated === undefined ? {} : { isEscalated, escalationReason }),
          }

      const { data, status } = await apiClient.post<{ groundTruthId: string }>(
        `/int/v3/engagements/audit/manual/resolution`,
        payload
      )

      return { data, status }
    },
  })
}

export function usePostAuditDiscrepancyTaskResolution(auditDiscrepancyTaskId?: string) {
  return useMutation({
    mutationFn: async (resolutionId: string | null) => {
      if (auditDiscrepancyTaskId) {
        const { data, status } = await apiClient.post<{ groundTruthId: string }>(
          `/int/v3/engagements/audit/discrepancy/session/${auditDiscrepancyTaskId}`,
          { selectedGroundTruthId: resolutionId }
        )

        return { data, status }
      }
    },
  })
}

export type ManualSessionStartRequest = {
  programIds?: string[]
  operatorIds?: string[]
  startDate?: string
  endDate?: string
  count?: number
}

export type ManualSessionStartParams = {
  data: ManualSessionStartRequest
}

export type ManualSessionEngagement = {
  engagementId: string
  programId: string
  defaultMediaId: string
  metadata: Record<string, unknown>
  predictions: Prediction[]
  media: Media[]
  pipelineJob: PipelineJob
  program: {
    name: string
    engagementLifetime: number
    operatorDisplayName: string
    displayName: string
    instructions: string
    color: string
    labelSet: string[]
    responseSet: string[]
    iconType: string
  }
}

export type ManualSearchSummary = Record<string, unknown>

export type ManualSessionStartResponse = {
  auditQueueTaskIds: string[]
  remainingCount: number
  searchSummary?: ManualSearchSummary
}

const MANUAL_AUDIT_SESSION_REQUEST_TIMEOUT = 90_000

export function useStartManualSession() {
  return useMutation({
    mutationFn: async ({ data: params }: ManualSessionStartParams) => {
      const { data } = await apiClient.post<ManualSessionStartResponse>(
        `/int/v3/engagements/audit/manual`,
        params,
        { timeout: MANUAL_AUDIT_SESSION_REQUEST_TIMEOUT }
      )
      return data
    },
  })
}
